// Which obstacle the car is touching, for the sound of a collision (src/audio/sfx-model.ts): the
// physics reports only how hard and in which direction the chassis was hit (trees, rocks and props
// are batched into compound colliders), so this keeps the tagged primitives in a grid and finds the
// one nearest the car's body when a hit comes in. Pure TS, no three.js.
import type { ObstacleMaterial, StaticCollider } from '../sim/sim';
import type { HitInput } from '../audio/sfx-model';

const CELL = 16;
/** Car body half-extents (m) in its own frame: across, along. */
const HALF_W = 0.95;
const HALF_L = 2.15;
/** A primitive this close to the body (m) counts as the one that was hit. */
const TOUCH = 0.7;

interface Obstacle {
  mat: ObstacleMaterial;
  x: number;
  z: number;
  /** Circle radius (cylinder, ball), or 0 for a box. */
  r: number;
  /** Box half-extents and yaw (cuboid). */
  hx: number;
  hz: number;
  cos: number;
  sin: number;
}

export interface ObstacleHit {
  mat: ObstacleMaterial;
  /** Contact point in the car's frame (m): +left, +forward. */
  left: number;
  forward: number;
}

export class ObstacleIndex {
  private readonly cells = new Map<number, Obstacle[]>();
  private readonly hit: ObstacleHit = { mat: 'wood', left: 0, forward: 0 };

  constructor(colliders: readonly StaticCollider[]) {
    for (const c of colliders) {
      if (c.kind !== 'cylinder' && c.kind !== 'cuboid' && c.kind !== 'ball') continue;
      if (!c.mat) continue;
      const o: Obstacle =
        c.kind === 'cuboid'
          ? { mat: c.mat, x: c.pos[0], z: c.pos[2], r: 0, hx: c.half[0], hz: c.half[2], cos: Math.cos(c.yaw), sin: Math.sin(c.yaw) }
          : { mat: c.mat, x: c.pos[0], z: c.pos[2], r: c.radius, hx: 0, hz: 0, cos: 1, sin: 0 };
      // A box can reach into the neighbouring cells: file it under every cell its bounding circle touches.
      const reach = o.r + Math.hypot(o.hx, o.hz);
      for (let gx = Math.floor((o.x - reach) / CELL); gx <= Math.floor((o.x + reach) / CELL); gx++) {
        for (let gz = Math.floor((o.z - reach) / CELL); gz <= Math.floor((o.z + reach) / CELL); gz++) {
          const k = key(gx, gz);
          let list = this.cells.get(k);
          if (!list) this.cells.set(k, (list = []));
          list.push(o);
        }
      }
    }
  }

  /**
   * The tagged obstacle touching the car body at (x, z) heading along (fx, fz) (unit, on the ground
   * plane), or null (open ground, the terrain, the road). The returned object is reused.
   */
  touching(x: number, z: number, fx: number, fz: number): ObstacleHit | null {
    // Car frame: left = up × forward = (fz, −fx).
    const lx = fz;
    const lz = -fx;
    let best: Obstacle | null = null;
    let bestD = TOUCH;
    let bestQx = 0;
    let bestQz = 0;
    const g0x = Math.floor((x - CELL / 2) / CELL);
    const g0z = Math.floor((z - CELL / 2) / CELL);
    for (let gx = g0x; gx <= g0x + 1; gx++) {
      for (let gz = g0z; gz <= g0z + 1; gz++) {
        const list = this.cells.get(key(gx, gz));
        if (!list) continue;
        for (const o of list) {
          // Point of the obstacle closest to the car's centre.
          let qx: number;
          let qz: number;
          if (o.r > 0) {
            const dx = x - o.x;
            const dz = z - o.z;
            const d = Math.hypot(dx, dz) || 1;
            qx = o.x + (dx / d) * o.r;
            qz = o.z + (dz / d) * o.r;
          } else {
            // Into the box's frame (yaw about +Y: its local +Z is (sin, cos)), clamp, back out.
            const dx = x - o.x;
            const dz = z - o.z;
            const u = Math.max(-o.hx, Math.min(o.hx, dx * o.cos - dz * o.sin));
            const v = Math.max(-o.hz, Math.min(o.hz, dx * o.sin + dz * o.cos));
            qx = o.x + u * o.cos + v * o.sin;
            qz = o.z - u * o.sin + v * o.cos;
          }
          // Distance from that point to the car's body box.
          const px = qx - x;
          const pz = qz - z;
          const side = px * lx + pz * lz;
          const along = px * fx + pz * fz;
          const d = Math.hypot(Math.max(0, Math.abs(side) - HALF_W), Math.max(0, Math.abs(along) - HALF_L));
          if (d < bestD) {
            bestD = d;
            best = o;
            bestQx = side;
            bestQz = along;
          }
        }
      }
    }
    if (!best) return null;
    this.hit.mat = best.mat;
    this.hit.left = bestQx;
    this.hit.forward = bestQz;
    return this.hit;
  }
}

/**
 * A chassis contact for the effects: what was hit and where on the car. `force` (N) and `dirY` (the
 * vertical part of its unit direction) come from the physics, the car at (x, z) heading (fx, fz).
 * A tagged obstacle touching the body wins; otherwise a mostly vertical push is the ground under the
 * car, and a sideways one the bank beside the road, on whichever side the ground rises. Writes `out`.
 */
export function classifyContact(index: ObstacleIndex, heightAt: (x: number, z: number) => number, force: number, dirY: number, x: number, z: number, fx: number, fz: number, out: HitInput): HitInput {
  const n = Math.hypot(fx, fz) || 1;
  fx /= n;
  fz /= n;
  out.force = force;
  const o = index.touching(x, z, fx, fz);
  if (o) {
    out.kind = o.mat;
    out.left = o.left;
    out.forward = o.forward;
    return out;
  }
  out.forward = 0;
  if (Math.abs(dirY) > 0.6) {
    out.kind = 'ground';
    out.left = 0;
    return out;
  }
  const lx = fz;
  const lz = -fx;
  const rise = heightAt(x + lx * 1.6, z + lz * 1.6) - heightAt(x - lx * 1.6, z - lz * 1.6);
  out.kind = 'bank';
  out.left = rise >= 0 ? 1.1 : -1.1;
  return out;
}

const key = (gx: number, gz: number) => (gx + 32768) * 65536 + (gz + 32768);
